/**
 * usePOSPrint - Custom hook for POS print logic
 * Extracted from POSPage.jsx to reduce component complexity.
 * Supports iMin, Bluetooth, QZ Tray (Windows), and browser fallback printing.
 */
import { toast } from "sonner";
import { api } from "@/store/useStore";
import { bluetoothPrinter } from "@/lib/bluetooth-printer";
import {
  generateReceiptHTML,
  openPrintWindow,
  generateShiftSummaryHTML,
  generateShiftSummaryLines,
} from "@/lib/print-utils";
import iminPrinter, {
  isIminDevice,
  printReceiptImin,
  printTextBlock as printIminTextBlock,
  openCashDrawer as openIminCashDrawer,
} from "@/services/iminPrinter";
import qzPrinter from "@/services/qzTrayPrinter";
import { printerManager } from "@/lib/printer-manager";

/**
 * @param {Object} opts
 * @param {Object} opts.currentDevice - Registered POS device
 * @param {Array}  opts.printDestinations - Configured print destinations for outlet
 * @param {boolean} opts.isImin - Whether running on iMin device
 * @param {Object} opts.invoiceTemplate - Current invoice template settings
 * @param {Object} opts.currentOutlet - Active outlet
 * @param {Object} opts.user - Authenticated user
 */
const LAST_RECEIPT_KEY = "pos_last_receipt";

/** Receipts get a human date — print_data.date is sometimes a raw ISO timestamp. */
const receiptDate = (value) => {
  if (!value) return new Date().toLocaleDateString("en-KE");
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString("en-KE");
};

export function usePOSPrint({
  currentDevice,
  printDestinations,
  isImin,
  invoiceTemplate,
  currentOutlet,
  user,
}) {
  // ─── Resolve printer type & paper size for a destination ─────────────
  const resolvePrinter = (destination, destConfig) => {
    let printerType = "browser";
    let paperSize = "80mm";

    if (destConfig) {
      printerType = destConfig.printer_type || "browser";
      paperSize = destConfig.paper_size || "80mm";
    } else if (currentDevice) {
      printerType = currentDevice.printer_connection || "auto";
      paperSize = currentDevice.builtin_paper_size || "80mm";
    } else {
      const saved = localStorage.getItem("printSettings");
      const settings = saved ? JSON.parse(saved) : {};
      if (destination === "receipt" && settings.receiptPrinter === "bluetooth") printerType = "bluetooth";
    }
    return { printerType, paperSize };
  };

  // ─── Last receipt (for the "Reprint last receipt" shortcut) ──────────
  const rememberLastReceipt = (printData, template) => {
    try {
      localStorage.setItem(
        LAST_RECEIPT_KEY,
        JSON.stringify({ printData, template, savedAt: new Date().toISOString() })
      );
    } catch (_) {
      // a full storage quota must never block a sale
    }
  };

  const getLastReceipt = () => {
    try {
      const saved = localStorage.getItem(LAST_RECEIPT_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (_) {
      return null;
    }
  };

  // ─── Generate print HTML for browser fallback ────────────────────────
  const generatePrintHTMLLocal = (data) =>
    generateReceiptHTML(data, invoiceTemplate || {});

  // ─── Browser print helper ───────────────────────────────────────────
  const printReceiptBrowser = async (data, reservedWindow = null) => {
    const template = data.template || invoiceTemplate || {};
    const receiptHtml = generateReceiptHTML(data, template);
    const opened = openPrintWindow(
      receiptHtml,
      `Receipt - ${data.invoice_number || data.order_number}`,
      reservedWindow
    );
    if (!opened) {
      throw new Error("Your browser blocked the receipt window. Allow pop-ups for this POS site and try again.");
    }
    return true;
  };

  const closeReservedReceiptWindow = (receiptWindow) => {
    if (receiptWindow && !receiptWindow.closed) receiptWindow.close();
  };

  // Must run synchronously from the Pay button click. Opening a new receipt
  // window after the close/eTIMS API calls finish loses the browser's user
  // activation and the print dialog never appears.
  const reserveBrowserReceiptWindow = () => {
    const hasIminReceiptPrinter = isImin && ["auto", "builtin"].includes(
      currentDevice?.printer_connection || ""
    );
    const hasBluetoothReceiptPrinter =
      (currentDevice?.printer_connection === "bluetooth" && bluetoothPrinter.isConnected) ||
      (!currentDevice && bluetoothPrinter.isConnected);
    const hasWindowsReceiptPrinter = printDestinations.some(
      (destination) =>
        destination.destination_type === "receipt" &&
        destination.printer_type === "windows" &&
        destination.auto_print &&
        destination.windows_printer_name
    );

    if (hasIminReceiptPrinter || hasBluetoothReceiptPrinter || hasWindowsReceiptPrinter) {
      return null;
    }

    const receiptWindow = window.open("", "_blank", "width=300,height=600");
    if (receiptWindow) {
      receiptWindow.document.write("<title>Preparing receipt</title><p>Preparing receipt...</p>");
      receiptWindow.document.close();
    }
    return receiptWindow;
  };

  // ─── Auto-print after settlement ───────────────────────────────────
  const handleAutoPrint = async (printData, reservedWindow = null) => {
    if (!printData) {
      closeReservedReceiptWindow(reservedWindow);
      return false;
    }

    try {
      const template = printData.template || invoiceTemplate || {};
      rememberLastReceipt(printData, template);
      let printerType = "browser";
      let paperSize = template.paper_size || "80mm";

      if (currentDevice) {
        printerType = currentDevice.printer_connection || "auto";
        paperSize =
          template.paper_size ||
          currentDevice.builtin_paper_size ||
          "80mm";
      } else {
        const saved = localStorage.getItem("printSettings");
        const settings = saved ? JSON.parse(saved) : {};
        if (settings.receiptPrinter === "bluetooth") printerType = "bluetooth";
      }

      // iMin device
      if (
        (printerType === "auto" || printerType === "builtin") &&
        isImin
      ) {
        try {
          const receiptForImin = {
            logoUrl:
              template.logo_base64 || template.logo_url || printData.logo_url,
            logoWidth: template.logo_width || 150,
            logoHeight: template.logo_height || 60,
            businessName: printData.business_name,
            businessAddress: printData.business_address,
            businessPhone: printData.business_phone,
            kraPin: printData.kra_pin,
            invoiceNumber:
              printData.invoice_number || printData.order_number,
            date: receiptDate(printData.date),
            time: new Date().toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            }),
            serverName:
              template.show_server_name !== false
                ? printData.served_by
                : null,
            customerName:
              template.show_customer_details !== false
                ? printData.customer_name
                : null,
            items: printData.items,
            groupByCategory: template.group_products_by_category,
            subtotal: printData.subtotal,
            tax: printData.tax_total || printData.tax,
            showDetailedTaxes: template.show_detailed_taxes,
            taxBreakdown: printData.tax_breakdown || [],
            serviceCharge: printData.service_charge,
            discount: printData.discount,
            total: printData.total,
            paymentMethod:
              template.show_payment_method !== false
                ? printData.payment_method
                : null,
            mpesaReceipt: printData.mpesa_receipt,
            pointsEarned:
              template.show_loyalty_points !== false
                ? printData.points_earned
                : 0,
            headerItems: template.header_items || [],
            footerItems: template.footer_items || [],
            labels: template.labels || {},
            showQrCode: template.show_qr_code,
            qrCodeUrl: template.qr_code_url,
            etims: printData.etims,
            isReprint:
              Boolean(printData.is_reprint) || Number(printData.print_count || 1) > 1,
          };
          await printReceiptImin(receiptForImin, paperSize);
          closeReservedReceiptWindow(reservedWindow);
          toast.success(`Receipt printed (${paperSize})`);
          return true;
        } catch (_) {
          // fall through
        }
      }

      // Bluetooth
      if (printerType === "bluetooth" && bluetoothPrinter.isConnected) {
        try {
          await bluetoothPrinter.printReceipt({
            ...printData,
            showDetailedTaxes: template.show_detailed_taxes,
            taxBreakdown: printData.tax_breakdown || [],
            etims: printData.etims,
          });
          closeReservedReceiptWindow(reservedWindow);
          toast.success("Receipt printed with eTIMS QR code");
          return true;
        } catch (_) {
          // fall through
        }
      }

      // QZ Tray auto-print for receipt destination
      const receiptDest = printDestinations.find(
        (d) =>
          d.destination_type === "receipt" &&
          d.printer_type === "windows" &&
          d.auto_print
      );

      if (receiptDest?.windows_printer_name) {
        try {
          const connected = await qzPrinter.connect();
          if (connected) {
            const receiptData = {
              businessName:
                printData.property_name || printData.business_name,
              businessAddress:
                printData.property_address || printData.business_address,
              businessPhone:
                printData.property_phone || printData.business_phone,
              invoiceNumber:
                printData.invoice_number || printData.order_number,
              waiterName: printData.served_by,
              customerName: printData.customer_name,
              items: printData.items,
              subtotal: printData.subtotal,
              tax: printData.tax,
              taxBreakdown: printData.tax_breakdown,
              discount: printData.discount,
              total: printData.total,
              paymentMethod: printData.payment_method,
              amountPaid: printData.amount_paid,
              changeAmount: printData.change_amount,
              isReprint: printData.print_count > 1,
              printCount: printData.print_count,
              etims: printData.etims,
              kraPin: printData.kra_pin,
              template: template || {},
            };
            await qzPrinter.printReceipt(
              receiptDest.windows_printer_name,
              receiptData,
              receiptDest.paper_size || paperSize
            );
            closeReservedReceiptWindow(reservedWindow);
            toast.success(
              `Receipt printed to ${receiptDest.windows_printer_name}`
            );
            return true;
          }
        } catch (_) {
          // fall through
        }
      }

      // Browser fallback
      return await printReceiptBrowser({ ...printData, template }, reservedWindow);
    } catch (_) {
      closeReservedReceiptWindow(reservedWindow);
      // auto-print failure shouldn't block the flow
      return false;
    }
  };

  // ─── Print invoice by ID (customer receipt) ─────────────────────────
  const handlePrintInvoice = async (invoiceId, destination = "receipt") => {
    try {
      const result = await api.post(
        `/invoices/${invoiceId}/print?destination=${destination}`
      );
      const printData = result.print_data;

      const destConfig = printDestinations.find(
        (d) => d.destination_type === destination
      );

      const { printerType, paperSize } = resolvePrinter(
        destination,
        destConfig
      );

      // iMin
      if (
        (printerType === "auto" || printerType === "builtin") &&
        isImin
      ) {
        try {
          {
            const receiptForImin = {
              logoUrl:
                invoiceTemplate?.logo_base64 ||
                printData.logo_url ||
                invoiceTemplate?.logo_url,
              businessName: printData.property_name,
              businessAddress: printData.property_address,
              headerItems: invoiceTemplate?.header_items || [],
              invoiceNumber:
                printData.invoice_number || printData.order_number,
              date: receiptDate(printData.date),
              time: new Date().toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              }),
              serverName: printData.served_by,
              customerName: printData.customer_name,
              items: printData.items,
              groupByCategory: invoiceTemplate?.group_by_category,
              groupedItems: printData.grouped_items,
              subtotal: printData.subtotal,
              tax: printData.tax,
              showDetailedTaxes: invoiceTemplate?.show_detailed_taxes,
              taxBreakdown: printData.tax_breakdown,
              discount: printData.discount,
              total: printData.total,
              paymentMethod: printData.payment_method,
              pointsEarned: printData.points_earned || 0,
              footerItems: invoiceTemplate?.footer_items || [],
              showQrCode: invoiceTemplate?.show_qr_code,
              qrCodeUrl: invoiceTemplate?.qr_code_url,
              labels: invoiceTemplate?.labels || {},
              isReprint: printData.is_reprint,
              etims: printData.etims,
            };
            await printReceiptImin(receiptForImin, paperSize);
            toast.success(`Receipt printed (iMin ${paperSize})`);
          }
          return;
        } catch (_) {
          // fall through
        }
      }

      // Bluetooth
      if (printerType === "bluetooth") {
        const btPrinterName =
          currentDevice?.bluetooth_printer_name ||
          destConfig?.bluetooth_name;

        if (bluetoothPrinter.isConnected || btPrinterName) {
          try {
            {
              await bluetoothPrinter.printReceipt({
                businessName: printData.property_name,
                address: printData.property_address,
                phone: printData.property_phone,
                receiptNumber:
                  printData.invoice_number || printData.order_number,
                date: receiptDate(printData.date),
                customerName: printData.customer_name,
                servedBy: printData.served_by,
                items: printData.items,
                subtotal: printData.subtotal,
                taxes: printData.tax_breakdown,
                tax: printData.tax,
                discount: printData.discount,
                total: printData.total,
                paymentMethod: printData.payment_method,
                mpesaReceipt: printData.mpesa_receipt,
                isReprint: printData.is_reprint,
                footer: "Thank you for shopping with us!",
              });
            }
            toast.success(`Printed to ${destination} (Bluetooth)`);
            return;
          } catch (_) {
            // fall through
          }
        }
      }

      // QZ Tray (Windows)
      if (
        printerType === "windows" &&
        destConfig?.windows_printer_name
      ) {
        try {
          const connected = await qzPrinter.connect();
          if (connected) {
            {
              const receiptData = {
                businessName:
                  printData.property_name || printData.business_name,
                businessAddress:
                  printData.property_address ||
                  printData.business_address,
                businessPhone:
                  printData.property_phone || printData.business_phone,
                invoiceNumber:
                  printData.invoice_number || printData.order_number,
                waiterName: printData.served_by,
                customerName: printData.customer_name,
                items: printData.items,
                subtotal: printData.subtotal,
                tax: printData.tax,
                taxBreakdown: printData.tax_breakdown,
                discount: printData.discount,
                total: printData.total,
                paymentMethod: printData.payment_method,
                amountPaid: printData.amount_paid,
                changeAmount: printData.change_amount,
                isReprint: printData.print_count > 1,
                printCount: printData.print_count,
                etims: printData.etims,
                kraPin: printData.kra_pin,
                template: invoiceTemplate || {},
              };
              await qzPrinter.printReceipt(
                destConfig.windows_printer_name,
                receiptData,
                paperSize
              );
            }
            toast.success(
              `Printed to ${destConfig.windows_printer_name} (QZ Tray)`
            );
            return;
          }
        } catch (_) {
          // fall through
        }
      }

      // Browser fallback
      const printWindow = window.open("", "_blank", "width=300,height=600");
      if (printWindow) {
        const content = generatePrintHTMLLocal(printData);
        printWindow.document.write(content);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          printWindow.close();
        };
      }
      toast.success(result.message);
    } catch (error) {
      toast.error(error.message || "Print failed");
    }
  };

  // ─── Print shift summary ────────────────────────────────────────────
  const printShiftSummary = async (shiftId) => {
    try {
      const summary = await api.get(`/shifts/${shiftId}/summary`);
      const paperSize = invoiceTemplate?.paper_size || "80mm";
      const shiftHTML = generateShiftSummaryHTML(summary, paperSize);

      // iMin built-in printer (X report while open, Z report once closed)
      if (isIminDevice()) {
        try {
          const { title, lines } = generateShiftSummaryLines(summary);
          await printIminTextBlock(lines, { title });
          toast.success(`${title.split(" —")[0]} printed`);
          return;
        } catch (_) {
          // fall through
        }
      }

      // Try QZ Tray first
      const receiptDest = printDestinations.find(
        (d) =>
          d.destination_type === "receipt" &&
          d.printer_type === "windows" &&
          d.windows_printer_name
      );

      if (receiptDest?.windows_printer_name) {
        try {
          const connected = await qzPrinter.connect();
          if (connected) {
            await qzPrinter.printRaw(
              receiptDest.windows_printer_name,
              shiftHTML
            );
            toast.success("Shift summary printed");
            return;
          }
        } catch (_) {
          // fall through
        }
      }

      // Browser fallback
      openPrintWindow(shiftHTML, "Shift Summary");
    } catch (_) {
      // Don't block the close flow
    }
  };

  // ─── Print order receipt (quick sale mode) ──────────────────────────
  const printOrderReceipt = async (order, orderData, { items, getSubtotal, getTax, getTotal, paymentMethod, promoDiscount, loyaltyDiscount }) => {
    if (!printerManager.hasConnectedPrinter()) return;

    try {
      const receiptPrinter = printerManager.getPrinterByType("receipt");
      if (!receiptPrinter) return;

      await printerManager.printReceipt(receiptPrinter.id, {
        businessName: currentOutlet?.property_name || "HostPoint POS",
        address: currentOutlet?.address || "",
        phone: currentOutlet?.phone || "",
        receiptNumber: order.order_number,
        date: new Date().toLocaleString(),
        customerName: orderData.customer_name,
        servedBy: user?.full_name || user?.username,
        items: items.map((item) => ({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        subtotal: getSubtotal(),
        tax: getTax(),
        discount: promoDiscount + loyaltyDiscount,
        total: getTotal() - promoDiscount - loyaltyDiscount,
        paymentMethod,
        footer:
          invoiceTemplate?.footer_text || "Thank you for shopping with us!",
      });
    } catch (_) {
      // printing is optional
    }
  };

  // ─── Print offline receipt via Bluetooth ────────────────────────────
  const printOfflineReceipt = async (offlineOrder) => {
    if (!printerManager.hasConnectedPrinter()) return;

    try {
      const receiptPrinter = printerManager.getPrinterByType("receipt");
      if (!receiptPrinter) return;

      await printerManager.printReceipt(receiptPrinter.id, {
        ...offlineOrder.receipt_data,
        isOffline: true,
      });
    } catch (_) {
      // printing is optional
    }
  };

  // ─── Reprint the last receipt ────────────────────────────────────────
  const reprintLastReceipt = async () => {
    const saved = getLastReceipt();
    if (!saved?.printData) {
      toast.error("No receipt has been printed on this till yet");
      return false;
    }
    const printData = { ...saved.printData, is_reprint: true, print_count: (saved.printData.print_count || 1) + 1 };
    return handleAutoPrint({ ...printData, template: saved.template });
  };

  // ─── Cash drawer kick ────────────────────────────────────────────────
  const openCashDrawer = async () => {
    const result = await openIminCashDrawer();
    if (result.success) toast.success("Cash drawer opened");
    else toast.error(result.error || "Could not open the cash drawer");
    return result.success;
  };

  return {
    handleAutoPrint,
    handlePrintInvoice,
    printShiftSummary,
    printOrderReceipt,
    printOfflineReceipt,
    printReceiptBrowser,
    reserveBrowserReceiptWindow,
    closeReservedReceiptWindow,
    generatePrintHTMLLocal,
    reprintLastReceipt,
    openCashDrawer,
  };
}
